import { Wrench, PaintRoller, Droplets, Package, HousePlus, CookingPot, Cpu, ArrowRight, Check } from "lucide-react";
import { Reveal, Overline } from "@/lib/Reveal";
import { SERVICES, enquireAbout } from "@/lib/site";

const ICONS = { Wrench, PaintRoller, Droplets, Package, HousePlus, CookingPot, Cpu };

export default function ServicesGrid() {
    return (
        <section id="services" data-testid="services-section" className="py-20 lg:py-28 bg-[#F3F3EF]">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
                    <Reveal>
                        <Overline>What We Do</Overline>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-neutral-900 mt-4 leading-[1.05]">
                            Services built around
                            <br className="hidden sm:block" /> your property
                        </h2>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <p className="text-base text-neutral-600 leading-relaxed max-w-md">
                            From everyday repairs to full loft extensions — every service is delivered with the same
                            professional approach and attention to detail.
                        </p>
                    </Reveal>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {SERVICES.slice(0, 6).map((s, i) => {
                        const Icon = ICONS[s.icon];
                        return (
                            <Reveal key={s.id} delay={(i % 3) * 0.08} className="h-full">
                                <article
                                    data-testid={s.testid}
                                    className="group h-full bg-white rounded-2xl p-7 sm:p-8 border border-[#E5E5E0] shadow-[0_2px_12px_rgba(0,0,0,0.04)] transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1.5 hover:shadow-[0_20px_50px_rgba(0,0,0,0.10)] hover:border-[#E85A1D]/40 flex flex-col"
                                >
                                    <div className="w-12 h-12 rounded-xl bg-[#FFF1EB] flex items-center justify-center transition-colors duration-300 group-hover:bg-[#E85A1D]">
                                        <Icon className="w-6 h-6 text-[#E85A1D] transition-colors duration-300 group-hover:text-white" strokeWidth={2} />
                                    </div>
                                    <h3 className="font-display text-xl sm:text-2xl font-bold tracking-tight text-neutral-900 mt-6">
                                        {s.title}
                                    </h3>
                                    <p className="mt-3 text-sm text-neutral-600 leading-relaxed">{s.desc}</p>
                                    <ul className="mt-5 space-y-2">
                                        {s.points.map((p) => (
                                            <li key={p} className="flex items-center gap-2.5 text-sm text-neutral-700">
                                                <Check className="w-4 h-4 text-[#E85A1D] shrink-0" strokeWidth={2.5} />
                                                {p}
                                            </li>
                                        ))}
                                    </ul>
                                    <button
                                        onClick={() => enquireAbout(s.title)}
                                        className="mt-auto pt-7 flex items-center gap-2 text-sm font-bold text-neutral-900 hover:text-[#E85A1D] transition-colors duration-200"
                                        aria-label={`Enquire about ${s.title}`}
                                    >
                                        Learn More
                                        <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
                                    </button>
                                </article>
                            </Reveal>
                        );
                    })}

                    <Reveal delay={0.1} className="md:col-span-2 lg:col-span-3">
                        <article
                            data-testid="service-card-smart-home-solutions"
                            className="group bg-[#141414] rounded-2xl p-7 sm:p-10 flex flex-col lg:flex-row lg:items-center gap-8 noise-overlay relative overflow-hidden"
                        >
                            <div className="w-14 h-14 rounded-xl bg-[#E85A1D] flex items-center justify-center shrink-0">
                                <Cpu className="w-7 h-7 text-white" strokeWidth={2} />
                            </div>
                            <div className="flex-1">
                                <p className="font-mono-brand text-[11px] uppercase tracking-[0.25em] text-[#E85A1D] font-semibold">
                                    Growing Category
                                </p>
                                <h3 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-white mt-2">
                                    Smart Home & Property Solutions
                                </h3>
                                <p className="mt-3 text-sm sm:text-base text-white/65 leading-relaxed max-w-2xl">
                                    A flexible category for additional smart property services as they are introduced.
                                    Have a project that does not fit a standard box? Talk to us — if it improves your
                                    property, we want to hear about it.
                                </p>
                            </div>
                            <button
                                onClick={() => enquireAbout("Smart Home & Property Solutions")}
                                className="shrink-0 flex items-center gap-2 bg-white text-neutral-900 hover:bg-[#E85A1D] hover:text-white font-bold px-7 py-3.5 rounded-full text-sm transition-colors duration-200"
                            >
                                Learn More
                                <ArrowRight className="w-4 h-4" />
                            </button>
                        </article>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}
